import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Users,
  Wrench,
  Shield,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
  UserPlus,
  ThumbsUp,
  Image,
  Vote
} from 'lucide-react';

const AdminAnalytics = () => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [token]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const issueTypeLabels = {
    pothole: 'Potholes',
    streetlight: 'Streetlights',
    dumping: 'Illegal Dumping',
    signage: 'Signage',
    drainage: 'Drainage',
    road_damage: 'Road Damage',
    other: 'Other'
  };

  const issueTypeColors = {
    pothole: 'bg-orange-500',
    streetlight: 'bg-yellow-500',
    dumping: 'bg-red-500',
    signage: 'bg-blue-500',
    drainage: 'bg-cyan-500',
    road_damage: 'bg-purple-500',
    other: 'bg-slate-500'
  };

  const safetyTypeLabels = {
    suspicious: 'Suspicious Activity',
    vandalism: 'Vandalism',
    lighting: 'Poor Lighting',
    traffic: 'Traffic Hazard',
    other: 'Other'
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-plp-gold" />
            Analytics Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Detailed insights into community engagement and activity</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Users</p>
                <p className="text-3xl font-bold">{analytics?.users?.total || 0}</p>
                <p className="text-blue-200 text-xs mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  +{analytics?.users?.this_week || 0} this week
                </p>
              </div>
              <Users className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Volunteers</p>
                <p className="text-3xl font-bold">{analytics?.volunteers?.total || 0}</p>
                <p className="text-emerald-200 text-xs mt-1 flex items-center gap-1">
                  <UserPlus className="w-3 h-3" />
                  +{analytics?.volunteers?.this_month || 0} this month
                </p>
              </div>
              <Activity className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Pending Approvals</p>
                <p className="text-3xl font-bold">{analytics?.users?.pending_approvals || 0}</p>
                <p className="text-amber-200 text-xs mt-1">Awaiting review</p>
              </div>
              <Clock className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Issues Resolved</p>
                <p className="text-3xl font-bold">{analytics?.issues?.resolved_this_month || 0}</p>
                <p className="text-purple-200 text-xs mt-1">This month</p>
              </div>
              <CheckCircle className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="issues" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="issues" className="data-[state=active]:bg-plp-blue data-[state=active]:text-white">
            <Wrench className="w-4 h-4 mr-2" />
            Issues
          </TabsTrigger>
          <TabsTrigger value="safety" className="data-[state=active]:bg-plp-blue data-[state=active]:text-white">
            <Shield className="w-4 h-4 mr-2" />
            Safety
          </TabsTrigger>
          <TabsTrigger value="engagement" className="data-[state=active]:bg-plp-blue data-[state=active]:text-white">
            <Activity className="w-4 h-4 mr-2" />
            Engagement
          </TabsTrigger>
        </TabsList>

        <TabsContent value="issues" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Issues by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-plp-gold" />
                  Issues by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics?.issues?.by_type || {}).map(([type, count]) => {
                    const total = Object.values(analytics?.issues?.by_type || {}).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">{issueTypeLabels[type] || type}</span>
                          <span className="font-medium">{count} ({percentage}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${issueTypeColors[type] || 'bg-slate-400'} transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(analytics?.issues?.by_type || {}).length === 0 && (
                    <p className="text-slate-500 text-center py-4">No issues reported yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top Upvoted Issues */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ThumbsUp className="w-5 h-5 text-plp-gold" />
                  Most Upvoted Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(analytics?.issues?.top_upvoted || []).map((issue, idx) => (
                    <div key={issue.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <span className="w-6 h-6 bg-plp-gold text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{issue.title}</p>
                        <p className="text-xs text-slate-500 capitalize">{issue.status?.replace('_', ' ')}</p>
                      </div>
                      <span className="flex items-center gap-1 text-sm text-plp-blue font-medium">
                        <ThumbsUp className="w-4 h-4" />
                        {issue.upvotes || 0}
                      </span>
                    </div>
                  ))}
                  {(analytics?.issues?.top_upvoted || []).length === 0 && (
                    <p className="text-slate-500 text-center py-4">No upvoted issues yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Issues Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-800">{analytics?.issues?.this_month || 0}</p>
                  <p className="text-sm text-slate-500">New Issues</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-3xl font-bold text-emerald-600">{analytics?.issues?.resolved_this_month || 0}</p>
                  <p className="text-sm text-slate-500">Resolved</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{analytics?.users?.this_month || 0}</p>
                  <p className="text-sm text-slate-500">New Users</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-3xl font-bold text-purple-600">{analytics?.volunteers?.this_month || 0}</p>
                  <p className="text-sm text-slate-500">New Volunteers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="safety" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-plp-gold" />
                  Safety Reports by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics?.safety?.by_type || {}).map(([type, count]) => {
                    const total = Object.values(analytics?.safety?.by_type || {}).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">{safetyTypeLabels[type] || type}</span>
                          <span className="font-medium">{count} ({percentage}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500 transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(analytics?.safety?.by_type || {}).length === 0 && (
                    <p className="text-slate-500 text-center py-4">No safety reports yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Safety Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center p-8">
                  <p className="text-5xl font-bold text-red-500">{analytics?.safety?.reports_this_month || 0}</p>
                  <p className="text-slate-500 mt-2">Reports This Month</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="engagement" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <Vote className="w-12 h-12 mx-auto text-plp-blue mb-3" />
                <p className="text-3xl font-bold text-slate-800">{analytics?.engagement?.total_polls || 0}</p>
                <p className="text-slate-500">Community Polls</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <ThumbsUp className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                <p className="text-3xl font-bold text-slate-800">{analytics?.engagement?.total_votes || 0}</p>
                <p className="text-slate-500">Total Votes Cast</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Image className="w-12 h-12 mx-auto text-purple-500 mb-3" />
                <p className="text-3xl font-bold text-slate-800">{analytics?.engagement?.gallery_photos || 0}</p>
                <p className="text-slate-500">Gallery Photos</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAnalytics;
